/**
 * EncounterListPagination
 *
 * Pagination component for desktop view.
 * Enterprise design visually integrated with the table,
 * refined button styles, and better visual hierarchy.
 */

import React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useEncounterList } from '../EncounterListContext';
import { DEFAULT_PAGE_SIZE_OPTIONS } from '../types';

/**
 * Generate page numbers with ellipsis for large page counts
 */
function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];

  pages.push(1);

  if (currentPage > 3) {
    pages.push('...');
  }

  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    if (!pages.includes(i)) {
      pages.push(i);
    }
  }

  if (currentPage < totalPages - 2) {
    pages.push('...');
  }

  if (!pages.includes(totalPages)) {
    pages.push(totalPages);
  }

  return pages;
}

export function EncounterListPagination() {
  const { pagination, loading, setPage, setLimit, config } = useEncounterList();

  const { page, limit, total } = pagination;
  const totalPages = Math.ceil(total / limit) || 1;
  const startItem = total === 0 ? 0 : (page - 1) * limit + 1;
  const endItem = Math.min(page * limit, total);

  const canGoPrevious = page > 1;
  const canGoNext = page < totalPages;

  const pageSizeOptions = config.pageSizeOptions || DEFAULT_PAGE_SIZE_OPTIONS;

  const handlePrevious = () => {
    if (canGoPrevious && !loading) {
      setPage(page - 1);
    }
  };

  const handleNext = () => {
    if (canGoNext && !loading) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(e.target.value, 10);
    setLimit(newLimit);
  };

  if (total === 0) {
    return null;
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-gray-200 bg-gray-50/50">
      {/* Left side: Page size selector and info */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <label htmlFor="encounter-page-size" className="text-sm text-gray-500">
            Show
          </label>
          <select
            id="encounter-page-size"
            value={limit}
            onChange={handleLimitChange}
            disabled={loading}
            className="block w-20 rounded-lg border-gray-200 text-sm focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20 disabled:opacity-50 bg-white"
          >
            {pageSizeOptions.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
          <span className="text-sm text-gray-500">per page</span>
        </div>

        <div className="hidden sm:block text-sm text-gray-500">
          <span className="font-medium text-gray-700">{startItem}</span>
          {' - '}
          <span className="font-medium text-gray-700">{endItem}</span>
          {' of '}
          <span className="font-medium text-gray-700">{total}</span>
        </div>
      </div>

      {/* Right side: Navigation */}
      <div className="flex items-center gap-2">
        <div className="sm:hidden text-sm text-gray-500">
          Page <span className="font-medium text-gray-700">{page}</span> of <span className="font-medium text-gray-700">{totalPages}</span>
        </div>

        <nav className="flex items-center gap-1" aria-label="Pagination">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={!canGoPrevious || loading}
            className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Previous page"
          >
            <ChevronLeftIcon className="h-4 w-4 mr-1" />
            <span className="hidden sm:inline">Prev</span>
          </button>

          <div className="hidden md:flex items-center gap-1">
            {generatePageNumbers(page, totalPages).map((pageNum, idx) =>
              pageNum === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 text-sm">
                  ...
                </span>
              ) : (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setPage(pageNum as number)}
                  disabled={loading}
                  className={`w-9 h-9 text-sm font-medium rounded-lg transition-all ${
                    pageNum === page
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 bg-white border border-gray-200 hover:bg-gray-50'
                  } disabled:opacity-40`}
                >
                  {pageNum}
                </button>
              )
            )}
          </div>

          <button
            type="button"
            onClick={handleNext}
            disabled={!canGoNext || loading}
            className="relative inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Next page"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRightIcon className="h-4 w-4 ml-1" />
          </button>
        </nav>
      </div>
    </div>
  );
}

export default EncounterListPagination;
